/**
 * 합동 일러스트 단계별 레이어 구성.
 *
 * **컨셉** — 코드를 등록할 때마다 일러스트가 한 단계씩 채워지고, 3/3 도달 시
 * 풀컬러 완성본으로 교체된다. 서버는 단일 카운트만 다루므로 클라이언트도
 * 카운트 기반 레이어 결정만 한다 (스트리머별 분기는 두지 않는다).
 */

/**
 * 일러스트 원본 이미지 경로. public 디렉터리 기준 절대 URL.
 *
 * @deprecated 단계별 레이어가 도입되어 직접 사용 대신 `getIllustrationLayers`를 통해 조회한다.
 * 기존 외부 참조 호환을 위해 export는 유지한다.
 */
export const ILLUSTRATION_IMAGE_SRC = '/reward.png';

/**
 * 일러스트 단계별 베이스 이미지 경로.
 *
 * - 0/3: 시작 상태 — 1.png
 * - 1/3, 2/3: 진행 중 — 1.png 유지 (오버레이 패치를 위에 얹는다)
 * - 3/3: 완성 — reward.png 단일 노출
 */
const ILLUSTRATION_BASE_INITIAL = '/1.png';
const ILLUSTRATION_BASE_COMPLETE = '/reward.png';

/**
 * 일러스트 한 단계의 렌더링 정보.
 *
 * @property base - 항상 깔리는 베이스 이미지 경로 (필수)
 * @property overlay - 베이스 위에 얹히는 점진적 컷씬 이미지. 없으면 null.
 *   - 단일 슬롯이며, 단계 변경 시 React key를 통해 페이드인 트랜지션을 트리거한다.
 *   - 시각 위치는 CSS(`.overlayPatch`)에서 좌측 정렬·전체 높이·자연 비율 유지로
 *     처리되므로 데이터 단에는 좌표 정보를 두지 않는다.
 */
export interface IllustrationLayerConfig {
  readonly base: string;
  readonly overlay: {
    readonly src: string;
    /** React 재조정용 키. 같은 슬롯에서 src가 바뀌면 새로 마운트되어 페이드인이 재생된다. */
    readonly key: string;
  } | null;
}

/**
 * 등록된 코드 수(`total`)에 따른 일러스트 레이어 구성을 반환한다.
 *
 * - 0개: 베이스 1.png(흐릿한 베이스 컷), 오버레이 없음.
 * - 1개: 베이스 1.png, 오버레이 2.png (시로 등장 — 좌측에 페이드인).
 * - 2개: 베이스 1.png, 오버레이 3.png (시로 + 하루 등장 — 같은 슬롯에 페이드인 교체).
 * - 3개 이상: 베이스 reward.png 단일, 오버레이 없음 (3명 모두 등장한 최종 컷).
 *
 * @param total - 현재까지 등록된(검증된) 코드 개수
 * @returns 해당 단계의 레이어 구성 객체
 */
export function getIllustrationLayers(total: number): IllustrationLayerConfig {
  if (total >= 3) {
    return { base: ILLUSTRATION_BASE_COMPLETE, overlay: null };
  }
  if (total === 2) {
    return {
      base: ILLUSTRATION_BASE_INITIAL,
      overlay: { src: '/3.png', key: 'overlay-step-3' },
    };
  }
  if (total === 1) {
    return {
      base: ILLUSTRATION_BASE_INITIAL,
      overlay: { src: '/2.png', key: 'overlay-step-2' },
    };
  }
  return { base: ILLUSTRATION_BASE_INITIAL, overlay: null };
}
